import {
  ChangeFunction,
  ApproverGroup,
  ChangeFunctionApproverGroupApprovalRequirement
} from '@domain/shifthandover'
import {Column, DataTable} from '@hconnect/uikit'
import {Box, Checkbox} from '@mui/material'
import {FC, Dispatch, SetStateAction, useMemo} from 'react'

type FunctionRow = {
  id: string
  name: string
  [approverGroupId: string]: boolean | string
}

type ApproverMatrixTableProps = {
  functions: ChangeFunction[]
  approverGroups: ApproverGroup[]
  data: ChangeFunctionApproverGroupApprovalRequirement[]
  onChange: Dispatch<SetStateAction<ChangeFunctionApproverGroupApprovalRequirement[]>>
}

export const ApproverMatrixTable: FC<ApproverMatrixTableProps> = ({
  functions,
  approverGroups,
  onChange,
  data
}) => {
  const rows: FunctionRow[] = useMemo(() => {
    return functions.map((functionData) => {
      const row: FunctionRow = {...functionData}
      approverGroups.forEach(({id}) => {
        const found = data.find((d) => d.functionId === functionData.id && d.approverGroupId === id)
        row[id] = found?.approvalRequired ?? false
      })
      return row
    })
  }, [data, functions, approverGroups])

  const columns: Column<FunctionRow>[] = useMemo(() => {
    const base: Column<FunctionRow>[] = [
      {
        key: 'functionId',
        label: 'Function',
        customTemplate: ({name}) => name
      }
    ]

    const groupCols: Column<FunctionRow>[] = approverGroups.map(({id, name}) => ({
      key: id,
      label: name,
      align: 'center',
      customTemplate: (item) => {
        const checked = Boolean(item[id])
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={checked}
              onChange={(e) => {
                const newChecked = e.target.checked
                onChange((prev) =>
                  prev.map((entry) =>
                    entry.functionId === item.id && entry.approverGroupId === id
                      ? {...entry, approvalRequired: newChecked}
                      : entry
                  )
                )
              }}
              data-test-id={`approver-matrix-checkbox-${item.id}-${id}`}
            />
          </Box>
        )
      }
    }))

    return [...base, ...groupCols]
  }, [approverGroups, onChange])

  return <DataTable<FunctionRow> columns={columns} data={rows} keyExtractor={(row) => row.id} />
}
